import { useState } from "react";
import { toast } from "sonner";
import { Clock, Download, Loader2, ShieldCheck, Upload, XCircle } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { fileToLogo } from "../lib/image";
import { fmtFull, parseNum, rp } from "../lib/format";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";

const PayInfo = ({ p }) => (
  <div className="rounded-2xl border p-5 space-y-3" data-testid="license-payment-info">
    <div className="flex items-baseline gap-2 flex-wrap">
      <span className="font-display text-3xl font-extrabold" data-testid="license-price">{rp(p.price)}</span>
      <span className="text-sm text-muted-foreground">{p.price_label}</span>
    </div>
    <pre className="whitespace-pre-wrap font-sans text-sm rounded-lg bg-muted/60 p-3" data-testid="license-bank-info">{p.bank_info}</pre>
    {p.qris_image && <img src={p.qris_image} alt="QRIS" className="w-48 rounded-lg border bg-white p-2" data-testid="license-qris" />}
    {p.instructions && <p className="text-sm text-muted-foreground">{p.instructions}</p>}
  </div>
);

const SubmitForm = ({ price, onDone }) => {
  const [f, setF] = useState({ sender_name: "", amount: String(price || ""), proof: "", note: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.proof) return toast.error("Unggah foto/screenshot bukti transfer");
    setBusy(true);
    try {
      await api.post("/license/submit", { ...f, amount: parseNum(f.amount) });
      toast.success("Bukti pembayaran terkirim, menunggu persetujuan");
      onDone();
    } catch (e2) {
      toast.error(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="license-submit-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label>Nama pengirim / rekening</Label><Input required value={f.sender_name} onChange={(e) => setF({ ...f, sender_name: e.target.value })} data-testid="license-sender-input" /></div>
        <div className="space-y-1.5"><Label>Nominal ditransfer</Label><Input required value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="license-amount-input" /></div>
      </div>
      <div className="space-y-1.5">
        <Label>Bukti transfer (gambar)</Label>
        <Input type="file" accept="image/*" onChange={async (e) => e.target.files[0] && setF({ ...f, proof: await fileToLogo(e.target.files[0], 1400) })} data-testid="license-proof-input" />
        {f.proof && <img src={f.proof} alt="Bukti" className="h-32 rounded-lg border object-contain" />}
      </div>
      <div className="space-y-1.5"><Label>Catatan (opsional)</Label><Textarea value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="license-note-input" /></div>
      <Button type="submit" disabled={busy} data-testid="license-submit-btn">{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}Kirim Bukti Pembayaran</Button>
    </form>
  );
};

export const LicensePanel = ({ data, reload }) => {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.post("/desktop/download-link");
      window.location.href = `${process.env.REACT_APP_BACKEND_URL}${d.url}`;
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const { status, license: lic, payment } = data;
  if (status === "approved") return (
    <div className="space-y-3" data-testid="license-approved">
      <div className="flex items-center gap-2 text-sm font-semibold text-success"><ShieldCheck className="h-5 w-5" />Lisensi aktif — installer siap diunduh</div>
      <Button size="lg" className="h-12 px-6 text-base" onClick={download} disabled={busy} data-testid="download-desktop-btn">
        {busy ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <Download className="h-5 w-5 mr-2" />}Unduh untuk Windows
      </Button>
    </div>
  );
  if (status === "pending") return (
    <div className="rounded-2xl border border-warning/40 bg-warning/10 p-5 text-sm space-y-1" data-testid="license-pending">
      <div className="flex items-center gap-2 font-semibold"><Clock className="h-5 w-5" />Menunggu persetujuan admin</div>
      <p className="text-muted-foreground">Bukti dikirim {fmtFull(lic.submitted_at)}. Anda akan menerima notifikasi setelah disetujui.</p>
    </div>
  );
  return (
    <div className="space-y-5" data-testid="license-required">
      {status === "rejected" && (
        <div className="rounded-xl border border-destructive/40 bg-destructive/10 p-4 text-sm flex gap-2" data-testid="license-rejected">
          <XCircle className="h-5 w-5 text-destructive shrink-0" />Pengajuan sebelumnya ditolak. {lic?.reject_reason && `Alasan: ${lic.reject_reason}.`} Silakan kirim ulang.
        </div>
      )}
      <p className="text-sm text-muted-foreground">Installer desktop khusus member berlisensi. Lakukan pembayaran lalu unggah bukti transfer.</p>
      <PayInfo p={payment} />
      <SubmitForm price={payment.price} onDone={reload} />
    </div>
  );
};
